"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/steps/PlaidInvestListStep";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import { PlaidInstitutionCard } from "../shared/PlaidInstitutionCard";
import { PlaidSecurityBadge } from "../shared/PlaidSecurityBadge";
import { usePlaidBrokers } from "../hooks/usePlaidBrokers";
import type { PositionBroker } from "@features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidInvestListStepProps {
  onSelectBroker: (broker: PositionBroker) => void;
  onConnectOthers: () => void;
  onAddManually: () => void;
  /** Onboarding 入口隐藏手动新增（仅允许 Plaid） */
  showAddManually?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
}

export const PlaidInvestListStep: React.FC<PlaidInvestListStepProps> = ({
  onSelectBroker,
  onConnectOthers,
  onAddManually,
  showAddManually = true,
  onBack,
  onClose,
  isLoading: isPlaidLoading = false,
}) => {
  const { data: brokers, isLoading: isBrokersLoading } =
    usePlaidBrokers("INVEST");

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-background_solid_secondary text-content_primary">
      {/* Top Nav Header — fixed outside the scroll area */}
      <div className="flex items-center justify-between w-full shrink-0 min-h-[44px] px-4 pt-3 md:px-6 md:pt-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            data-openreplay-label="plaidconnectflow_step_back"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Go back")}
          >
            <Icon name="left-arrow-alt" size={20} />
          </button>
        ) : (
          <div className="size-10" />
        )}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-openreplay-label="plaidconnectflow_step_close"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Close")}
          >
            <Icon name="close" size={20} />
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-3 md:px-6 md:pb-4">
        {/* Main Content */}
        <div className="flex flex-col max-w-[361px] mx-auto w-full pt-4 pb-8 gap-6">
          {/* Intro Copy */}
          <div className="flex flex-col gap-2 w-full text-left">
            <h1 className="font-['Lexend'] text-2xl font-normal text-content_primary leading-8 tracking-[-0.96px]">
              {I18n.t("Start with where you invest")}
            </h1>
            <p className="text-base text-content_secondary leading-6 font-normal">
              {I18n.t(
                "You came in worried about your portfolio. Link it and I read the real holdings instead of guessing.",
              )}
            </p>
          </div>

          {/* Popular Institutions */}
          <div className="flex flex-col gap-3 w-full">
            <p className="text-xs font-medium text-content_tertiary leading-4">
              {I18n.t("Popular Institutions")}
            </p>

            <div className="flex flex-col gap-3 w-full">
              {/* Loading Skeletons */}
              {isBrokersLoading
                ? Array.from({ length: 6 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="w-full h-15 rounded-xl bg-background_solid_primary p-3 flex items-center gap-3 animate-pulse border border-border_solid/20"
                    >
                      <div className="size-9 rounded-full bg-background_solid_tertiary shrink-0" />
                      <div className="h-4 w-28 rounded bg-background_solid_tertiary" />
                    </div>
                  ))
                : brokers && brokers.length > 0
                  ? brokers.map((broker) => (
                      <PlaidInstitutionCard
                        key={broker.broker_id ?? broker.name}
                        name={broker.name ?? ""}
                        logoUrl={broker.broker_icon}
                        /* or-label: n/a —— label 在共享组件 PlaidInstitutionCard 内（plaidconnectflow_institution_tap），此处仅传回调 */
                        onClick={() => onSelectBroker(broker)}
                        disabled={isPlaidLoading}
                      />
                    ))
                  : null}

              {/* Connect Others Row */}
              <button
                type="button"
                onClick={onConnectOthers}
                disabled={isPlaidLoading}
                className="group w-full bg-background_solid_primary rounded-xl p-3 flex items-center gap-3 text-left active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50"
                data-openreplay-label="connect_others"
              >
                <div className="size-9 rounded-full bg-background_brand_overlay10 text-content_brand_primary flex items-center justify-center shrink-0">
                  <Icon name="link" size={18} />
                </div>
                <div className="flex flex-col gap-0.5 flex-1">
                  <span className="text-sm font-medium text-content_primary group-hover:text-content_brand_primary transition-colors">
                    {I18n.t("Connect others")}
                  </span>
                  <span className="text-xs text-content_tertiary leading-4">
                    {I18n.t("Over 12,000 institutions supported")}
                  </span>
                </div>
              </button>

              {/* Add Manually — dashed row (PRD / Figma); onboarding 仅允许 Plaid */}
              {showAddManually && (
                <button
                  type="button"
                  onClick={onAddManually}
                  className="group w-full rounded-xl p-3 flex items-center gap-3 text-left border border-dashed border-border_solid hover:border-border_brand_solid/60 active:scale-[0.99] transition-all cursor-pointer bg-transparent hover:bg-background_solid_primary"
                  data-openreplay-label="add_manually"
                >
                  <div className="size-9 rounded-full bg-background_brand_overlay10 text-content_brand_primary flex items-center justify-center shrink-0">
                    <Icon name="plus" size={18} />
                  </div>
                  <span className="text-sm font-medium text-content_primary group-hover:text-content_brand_primary transition-colors">
                    {I18n.t("Add manually")}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Bottom Trust Security Note */}
          <div className="w-full">
            <PlaidSecurityBadge />
          </div>
        </div>
      </div>
    </div>
  );
};
